/**
 * UndoToast — 4-second "Removed · Undo" toast.
 *
 * Usage:
 *   const { showUndo, toast } = useUndoToast();
 *   ...
 *   showUndo("Removed", () => revertDelete());
 *   {toast}
 *
 * Renders a small gold-edged pill at the bottom of the viewport (above
 * the bottom nav). After 4s with no interaction the toast disappears
 * and the delete is considered final. Clicking Undo calls the supplied
 * callback before the timer fires.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { RotateCcw } from "lucide-react";

const C = {
  ink:    "#0F1B17",
  cream:  "#F3EDDD",
  gold:   "#D7BB7A",
  goldSoft: "rgba(215, 187, 122, 0.55)",
};

export function useUndoToast() {
  const [state, setState] = useState(null); // { msg, onUndo } | null
  const timerRef = useRef(null);

  const dismiss = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    setState(null);
  }, []);

  const showUndo = useCallback((msg, onUndo, ms = 4000) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setState({ msg, onUndo });
    timerRef.current = setTimeout(() => setState(null), ms);
  }, []);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const handleUndo = () => {
    if (state?.onUndo) state.onUndo();
    dismiss();
  };

  const toast = (
    <AnimatePresence>
      {state && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="fixed left-1/2 -translate-x-1/2 z-[80] flex items-center gap-3 px-5 py-3 rounded-full"
          style={{
            bottom: "max(96px, calc(env(safe-area-inset-bottom, 0px) + 96px))",
            background: "rgba(15, 27, 23, 0.92)",
            border: `1px solid ${C.goldSoft}`,
            backdropFilter: "blur(10px)",
            WebkitBackdropFilter: "blur(10px)",
            boxShadow: `0 10px 26px rgba(0,0,0,0.4), 0 0 24px ${C.gold}22`,
          }}
          data-testid="undo-toast"
        >
          <span
            className="text-[12px] tracking-[0.18em] uppercase"
            style={{ color: C.cream, fontFamily: '"Manrope", sans-serif', fontWeight: 600 }}
          >
            {state.msg}
          </span>
          <button
            onClick={handleUndo}
            data-testid="undo-toast-btn"
            className="flex items-center gap-1.5 text-[11px] tracking-[0.22em] uppercase font-bold transition-all hover:scale-105"
            style={{
              color: C.gold,
              fontFamily: '"Manrope", sans-serif',
            }}
            aria-label="Undo delete"
          >
            <RotateCcw size={12} />Undo
          </button>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return { showUndo, toast, dismiss };
}
